/**
 * Predefined vehicle colours.
 *
 * The API stores colour as a free-text string (max 30 chars). This module maps
 * known colour names — Arabic and English — to hex values that the UI uses to
 * tint vehicle icons and render colour swatches.
 *
 * **Backward compatibility.** Existing vehicles have free-text colours like
 * `"أبيض"` or `"white"`. {@link resolveVehicleColor} normalises those to a
 * known entry or falls back to a neutral default so nothing breaks.
 */
export const VEHICLE_COLORS = [
    { key: 'أسود', hex: '#1C1C1E', border: null },
    { key: 'أبيض', hex: '#F5F5F5', border: '#C8C8CC' },
    { key: 'فضي', hex: '#A8A9AD', border: null },
    { key: 'رمادي', hex: '#6B6B6F', border: null },
    { key: 'أحمر', hex: '#C62A31', border: null },
    { key: 'أزرق', hex: '#2563EB', border: null },
    { key: 'أخضر', hex: '#137E45', border: null },
    { key: 'أصفر', hex: '#E5A100', border: '#C09000' },
    { key: 'برتقالي', hex: '#D97706', border: null },
    { key: 'بني', hex: '#6B3A10', border: null },
    { key: 'بيج', hex: '#D4C5A0', border: '#B5A880' },
    { key: 'بنفسجي', hex: '#7C3AED', border: null },
];
export const VEHICLE_COLOR_OTHER_KEY = 'أخرى';
const ENGLISH_TO_KEY = {
    black: 'أسود',
    white: 'أبيض',
    silver: 'فضي',
    grey: 'رمادي',
    gray: 'رمادي',
    red: 'أحمر',
    blue: 'أزرق',
    green: 'أخضر',
    yellow: 'أصفر',
    orange: 'برتقالي',
    brown: 'بني',
    beige: 'بيج',
    purple: 'بنفسجي',
};
const KEY_TO_ENTRY = new Map(VEHICLE_COLORS.map((e) => [e.key, e]));
const DEFAULT_COLOR = { key: '', hex: '#A8A9AD', border: null };
/**
 * Resolve a stored colour string to a known entry.
 *
 * Accepts Arabic keys, English names (case-insensitive), or free text. Returns
 * the matching entry or a neutral silver fallback so the UI always has a hex to
 * render.
 */
export function resolveVehicleColor(raw) {
    if (!raw || raw.trim() === '')
        return DEFAULT_COLOR;
    const trimmed = raw.trim();
    const exact = KEY_TO_ENTRY.get(trimmed);
    if (exact)
        return exact;
    const english = ENGLISH_TO_KEY[trimmed.toLowerCase()];
    if (english)
        return KEY_TO_ENTRY.get(english) ?? DEFAULT_COLOR;
    return DEFAULT_COLOR;
}
/**
 * Return the canonical Arabic key for a stored colour, or `null` if it is not
 * a predefined colour (Arabic or English).
 *
 * Used by the wizard to normalise legacy English values (e.g. `"white"` →
 * `"أبيض"`) so the colour selector pre-selects the correct swatch on Edit.
 */
export function normalizeColorKey(raw) {
    if (!raw || raw.trim() === '')
        return null;
    const trimmed = raw.trim();
    if (KEY_TO_ENTRY.has(trimmed))
        return trimmed;
    const english = ENGLISH_TO_KEY[trimmed.toLowerCase()];
    if (english)
        return english;
    return null;
}
/**
 * Whether a stored colour is one of the predefined keys (or the "other" sentinel).
 */
export function isKnownVehicleColor(raw) {
    if (!raw || raw.trim() === '')
        return false;
    const trimmed = raw.trim();
    return KEY_TO_ENTRY.has(trimmed) || trimmed === VEHICLE_COLOR_OTHER_KEY;
}
//# sourceMappingURL=vehicleColors.js.map